<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Upload, FileText, AlertCircle, CheckCircle2, Download } from 'lucide-vue-next'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import { useUiStore } from '@/stores/ui'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import { useEventOptions, useTicketsetOptions } from '@/composables/useLookups'
import ImportMappingStep from '@/components/domain/tickets/ImportMappingStep.vue'
import {
    parseFile,
    suggestMapping,
    suggestionsToMapping,
    applyMapping,
    rowsToJson,
    missingRequiredFields,
    isSupportedFile,
    ImportFileError,
    FIELD_KEYS,
    FIELD_LABELS,
    type ColumnMapping,
    type ImportError,
    type MappingSuggestion,
    type ParsedTable
} from '@/components/domain/tickets/import'

// 5krokový průvodce importem: nahrání → mapování → náhled → průběh → výsledek.
//
// Import je ATOMICKÝ — backend buď uloží všechno, nebo nic. Proto výsledek při
// chybě jednoznačně říká „neuložilo se nic" a nabízí návrat na mapování se
// zachovaným souborem.
const { t } = useI18n()
const router = useRouter()
const ui = useUiStore()
const toast = useToast()
const eventOptions = useEventOptions()
const ticketsetOptions = useTicketsetOptions()

type Step = 'upload' | 'mapping' | 'preview' | 'progress' | 'result'
const step = ref<Step>('upload')

const file = ref<File | null>(null)
const table = ref<ParsedTable | null>(null)
const suggestions = ref<Record<string, MappingSuggestion>>({})
const mapping = ref<ColumnMapping>({})

const importing = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedEventId = ref<string | null>(null)

type ImportResult = {
    ok: boolean
    created: number
    skipped: number
    ticketsetsCreated: number
    pricesCreated: number
    errorCount: number
    errors: ImportError[]
}
const result = ref<ImportResult | null>(null)

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('tickets.list.title'), to: '/tickets' }, { label: t('tickets.import.title') }])
})

/** Klientská validace — atomický import stejně spadne, tak ať to uživatel ví dřív. */
const mapped = computed(() => {
    if (!table.value) return { rows: [], errors: [] as ImportError[] }
    return applyMapping(table.value, mapping.value)
})

const missingFields = computed(() => missingRequiredFields(mapping.value))

const canContinueToPreview = computed(
    () =>
        missingFields.value.length === 0 &&
        table.value !== null &&
        table.value.headerConfidence !== 'low' &&
        eventIdAsInt() !== null
)

const canImport = computed(
    () =>
        mapped.value.rows.length > 0 &&
        mapped.value.errors.length === 0 &&
        eventIdAsInt() !== null &&
        !importing.value
)

const ticketsets = computed<string[]>(() => {
    const names = new Set<string>()
    for (const r of mapped.value.rows) if (r.ticketsetName) names.add(r.ticketsetName)
    return [...names].sort((a, b) => a.localeCompare(b, 'cs'))
})

const prices = computed<Record<string, string[]>>(() => {
    const map: Record<string, Set<string>> = {}
    for (const r of mapped.value.rows) {
        if (!r.ticketsetName || !r.priceName) continue
        ;(map[r.ticketsetName] ??= new Set()).add(r.priceName)
    }
    return Object.fromEntries(
        Object.entries(map).map(([k, v]) => [k, [...v].sort((a, b) => a.localeCompare(b, 'cs'))])
    )
})

const priceCount = computed(() => Object.values(prices.value).reduce((sum, arr) => sum + arr.length, 0))

const selectedEventName = computed(
    () => eventOptions.value.find(e => e.id === selectedEventId.value)?.name ?? '—'
)

async function loadFile(f: File, opts: Parameters<typeof parseFile>[1] = {}) {
    try {
        const parsed = await parseFile(f, opts)
        table.value = parsed
        file.value = f
        suggestions.value = suggestMapping(parsed.rows, parsed.headers)
        mapping.value = suggestionsToMapping(suggestions.value)
        step.value = 'mapping'
    } catch (err) {
        const message = err instanceof ImportFileError ? err.message : (err as Error).message
        toast.error(t('common.errors.generic'), message)
    }
}

function onFileSelect(e: Event) {
    const input = e.target as HTMLInputElement
    const f = input.files?.[0]
    if (!f) return
    if (!isSupportedFile(f.name)) {
        toast.error(t('tickets.import.onlyCsvXlsx'))
        input.value = ''
        return
    }
    loadFile(f)
}

function onDrop(e: DragEvent) {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0]
    if (!f) return
    if (!isSupportedFile(f.name)) {
        toast.error(t('tickets.import.onlyCsvXlsx'))
        return
    }
    loadFile(f)
}

/** Přepnutí hlavičky/listu/ořezu musí soubor přeparsovat — mění se tvar dat. */
function onHeaderChange(hasHeader: boolean) {
    if (file.value) {
        loadFile(file.value, {
            hasHeader,
            sheet: table.value?.activeSheet ?? undefined,
            skipRows: table.value?.skipRows,
            skipTrailingRows: table.value?.skipTrailingRows
        })
    }
}

function onSheetChange(sheet: string) {
    if (file.value) {
        loadFile(file.value, {
            sheet,
            skipRows: table.value?.skipRows,
            skipTrailingRows: table.value?.skipTrailingRows
        })
    }
}

/**
 * Ořez úvodních řádků. Detekce hlavičky se schválně nechá proběhnout znovu —
 * po odříznutí titulku bývá skutečná hlavička rozpoznatelná.
 */
function onSkipRowsChange(skipRows: number) {
    if (file.value) {
        loadFile(file.value, {
            skipRows,
            sheet: table.value?.activeSheet ?? undefined,
            skipTrailingRows: table.value?.skipTrailingRows
        })
    }
}

/** Ořez konce — souhrnné řádky („CELKEM“), poznámky pod daty. */
function onSkipTrailingRowsChange(skipTrailingRows: number) {
    if (file.value) {
        loadFile(file.value, {
            skipTrailingRows,
            skipRows: table.value?.skipRows,
            sheet: table.value?.activeSheet ?? undefined
        })
    }
}

/**
 * „Je to správně" — uživatel potvrdil odhad hlavičky. Předá se zpět jako
 * explicitní volba, takže `headerConfidence` spadne na `high` a varování zmizí.
 */
function onConfirmHeader() {
    if (file.value && table.value) {
        loadFile(file.value, {
            hasHeader: table.value.hasHeader,
            skipRows: table.value.skipRows,
            skipTrailingRows: table.value.skipTrailingRows,
            sheet: table.value.activeSheet ?? undefined
        })
    }
}

function eventIdAsInt(): number | null {
    if (!selectedEventId.value) return null
    const n = parseInt(selectedEventId.value, 10)
    return Number.isFinite(n) ? n : null
}

/**
 * Změna akce zruší výběr existující sady a ceny — patří ke konkrétní akci,
 * jinak by zůstaly viset u té předchozí.
 */
function onEventChange(id: string | null) {
    selectedEventId.value = id
    const next = { ...mapping.value }
    let changed = false
    for (const key of ['ticketsetName', 'priceName']) {
        if (next[key]?.kind === 'existing') {
            next[key] = { kind: 'empty' }
            changed = true
        }
    }
    if (changed) mapping.value = next
}

async function onConfirmImport() {
    const eventId = eventIdAsInt()
    if (eventId === null) {
        toast.error(t('tickets.import.eventRequired'))
        return
    }
    if (mapped.value.rows.length === 0) return

    step.value = 'progress'
    importing.value = true
    result.value = null

    try {
        const ticketsJson = rowsToJson(mapped.value.rows)
        const r = await sdk.AdminUploadTickets({ eventId, ticketsJson })
        const upload = r.admin?.ticket?.upload

        result.value = {
            ok: Boolean(upload?.ok),
            created: upload?.created ?? 0,
            skipped: upload?.skipped ?? 0,
            ticketsetsCreated: upload?.ticketsetsCreated ?? 0,
            pricesCreated: upload?.pricesCreated ?? 0,
            errorCount: upload?.errorCount ?? 0,
            errors: (upload?.errors ?? []).map(e => ({ row: e.row, message: e.message }))
        }

        step.value = 'result'
        if (result.value.ok) toast.success(t('tickets.import.success'))
        else toast.error(t('tickets.import.failed'))
    } catch (err) {
        result.value = {
            ok: false,
            created: 0,
            skipped: 0,
            ticketsetsCreated: 0,
            pricesCreated: 0,
            errorCount: 0,
            errors: []
        }
        step.value = 'result'
        toast.error((err as Error).message)
    } finally {
        importing.value = false
    }
}

/** Chyby ke stažení — u velkých souborů se v tabulce zobrazuje jen prvních 100. */
function downloadErrors() {
    const rows = result.value?.errors ?? []
    const csv = ['radek;chyba', ...rows.map(e => `${e.row};"${e.message.replace(/"/g, '""')}"`)].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `import-chyby-${file.value?.name ?? 'soubor'}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

/** Návrat na mapování zachová soubor i rozpracované mapování. */
function backToMapping() {
    step.value = 'mapping'
    result.value = null
}

function reset() {
    step.value = 'upload'
    file.value = null
    table.value = null
    suggestions.value = {}
    mapping.value = {}
    result.value = null
    selectedEventId.value = null
    if (fileInputRef.value) fileInputRef.value.value = ''
}

function stepClass(s: Step): string {
    return step.value === s ? 'px-2 py-1 rounded-4 bg-teal text-white' : 'px-2 py-1 rounded-4 bg-gray-100 text-gray-600'
}
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('tickets.import.title')">
            <template #actions>
                <BBtn severity="secondary" :label="t('common.actions.back')" @click="router.push('/tickets')" />
            </template>
        </PageHeader>

        <div class="flex items-center flex-wrap gap-2 mb-6 text-xs">
            <span :class="stepClass('upload')">{{ t('tickets.import.steps.upload') }}</span>
            <span class="text-gray-300">→</span>
            <span :class="stepClass('mapping')">{{ t('tickets.import.steps.mapping') }}</span>
            <span class="text-gray-300">→</span>
            <span :class="stepClass('preview')">{{ t('tickets.import.steps.preview') }}</span>
            <span class="text-gray-300">→</span>
            <span :class="stepClass('progress')">{{ t('tickets.import.steps.progress') }}</span>
            <span class="text-gray-300">→</span>
            <span :class="stepClass('result')">{{ t('tickets.import.steps.result') }}</span>
        </div>

        <!-- 1. Nahrání -->
        <div v-if="step === 'upload'" class="bg-white rounded-12 border border-gray-200 p-3 md:p-8">
            <div
                class="border-2 border-dashed border-gray-300 rounded-12 p-6 md:p-12 text-center hover:border-teal transition-colors duration-fast cursor-pointer"
                @dragover.prevent
                @drop="onDrop"
                @click="fileInputRef?.click()"
            >
                <Upload class="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p class="text-sm text-gray-600 mb-2">{{ t('tickets.import.dropzone') }}</p>
                <p class="text-xs text-gray-400">{{ t('tickets.import.columns') }}</p>
                <input
                    ref="fileInputRef"
                    type="file"
                    accept=".csv,.xls,.xlsx,.xlsm"
                    class="hidden"
                    @change="onFileSelect"
                />
            </div>
        </div>

        <!-- 2. Mapování -->
        <template v-if="step === 'mapping' && table">
            <div class="px-4 py-3 mb-3 bg-white rounded-12 border border-gray-200 flex items-center gap-3 flex-wrap">
                <FileText class="w-4 h-4 text-gray-500" />
                <span class="text-sm font-semibold text-ink">{{ file?.name }}</span>
            </div>

            <ImportMappingStep
                v-model="mapping"
                :table="table"
                :suggestions="suggestions"
                :event-options="eventOptions"
                :ticketset-options="ticketsetOptions"
                :selected-event-id="selectedEventId"
                @update:has-header="onHeaderChange"
                @update:sheet="onSheetChange"
                @update:skip-rows="onSkipRowsChange"
                @update:skip-trailing-rows="onSkipTrailingRowsChange"
                @update:selected-event-id="onEventChange"
                @confirm-header="onConfirmHeader"
            />

            <div
                class="flex items-center justify-between gap-2 px-4 py-3 mt-3 bg-white rounded-12 border border-gray-200 flex-wrap"
            >
                <p v-if="selectedEventId === null" class="text-xs text-red">
                    {{ t('tickets.import.eventRequired') }}
                </p>
                <p v-else-if="missingFields.length" class="text-xs text-red">
                    {{
                        t('tickets.import.mapping.missingRequired', {
                            fields: missingFields.map(f => FIELD_LABELS[f] ?? f).join(', ')
                        })
                    }}
                </p>
                <p v-else-if="table.headerConfidence === 'low'" class="text-xs text-amber-700">
                    {{ t('tickets.import.mapping.confirmHeaderFirst') }}
                </p>
                <span v-else />

                <div class="flex items-center gap-2 ml-auto">
                    <BBtn severity="secondary" :label="t('common.actions.back')" @click="reset" />
                    <BBtn :disabled="!canContinueToPreview" @click="step = 'preview'">
                        {{ t('tickets.import.mapping.continue') }}
                    </BBtn>
                </div>
            </div>
        </template>

        <!-- 3. Náhled -->
        <div v-if="step === 'preview' && table" class="bg-white rounded-12 border border-gray-200 overflow-hidden">
            <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-3 flex-wrap">
                <FileText class="w-4 h-4 text-gray-500" />
                <span class="text-sm font-semibold text-ink">{{ file?.name }}</span>
                <span class="text-xs text-gray-500">{{
                    t('tickets.import.rowCount', { n: mapped.rows.length })
                }}</span>
                <span class="text-xs text-gray-500">·</span>
                <span class="text-xs text-gray-600">{{
                    t('tickets.import.ticketsetCount', { n: ticketsets.length })
                }}</span>
                <span class="text-xs text-gray-500">·</span>
                <span class="text-xs text-gray-600">{{ t('tickets.import.priceCount', { n: priceCount }) }}</span>
                <span class="ml-auto text-xs">
                    <span v-if="mapped.errors.length" class="text-red font-semibold">{{
                        t('tickets.import.invalidRows', { n: mapped.errors.length })
                    }}</span>
                    <span v-else class="text-green font-semibold">{{
                        t('tickets.import.validRows', { n: mapped.rows.length })
                    }}</span>
                </span>
            </div>

            <!-- Atomický import: s chybami nemá smysl odesílat -->
            <div v-if="mapped.errors.length" class="px-4 py-3 border-b border-gray-200 bg-red-50">
                <p class="text-xs font-semibold text-red mb-1.5">
                    {{ t('tickets.import.blockingErrors', { n: mapped.errors.length }) }}
                </p>
                <ul class="text-xs text-red space-y-0.5 max-h-32 overflow-auto">
                    <li v-for="(e, i) in mapped.errors.slice(0, 20)" :key="i">
                        {{ t('tickets.import.rowLabel', { n: e.row }) }}: {{ e.message }}
                    </li>
                </ul>
            </div>

            <div v-if="ticketsets.length" class="px-4 py-3 border-b border-gray-200 bg-white text-xs space-y-1.5">
                <div v-for="ts in ticketsets" :key="ts" class="flex items-baseline gap-2">
                    <span class="font-semibold text-ink shrink-0">{{ ts }}</span>
                    <span class="text-gray-400 shrink-0">·</span>
                    <span class="text-gray-600">
                        <template v-for="p in prices[ts]" :key="p">
                            <span class="inline-block px-1.5 py-0.5 rounded-4 bg-teal-l text-teal-d font-medium mr-1">{{
                                p
                            }}</span>
                        </template>
                    </span>
                </div>
            </div>

            <!-- Akce se vybírá v mapování (určuje nabídku sad), tady jen potvrzení -->
            <div class="px-4 py-3 border-b border-gray-200 bg-white text-xs">
                <span class="text-gray-600">{{ t('tickets.import.event') }}:</span>
                <span class="font-semibold text-ink ml-1">{{ selectedEventName }}</span>
            </div>

            <div class="max-h-96 overflow-auto">
                <table class="text-xs whitespace-nowrap">
                    <thead class="bg-gray-50 sticky top-0 z-10">
                        <tr>
                            <th class="px-3 py-2 text-left">{{ t('tickets.import.preview.row') }}</th>
                            <th
                                v-for="col in FIELD_KEYS"
                                :key="col"
                                class="px-3 py-2 text-left font-semibold text-gray-700"
                            >
                                {{ t(`tickets.import.preview.cols.${col}`) }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in mapped.rows.slice(0, 100)" :key="r.sourceRow" class="border-b border-gray-200">
                            <td class="px-3 py-1.5 tabular-nums">{{ r.sourceRow }}</td>
                            <td
                                v-for="col in FIELD_KEYS"
                                :key="col"
                                class="px-3 py-1.5"
                                :class="col === 'code' ? 'font-mono' : ''"
                            >
                                {{ r[col] || '—' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="mapped.rows.length > 100" class="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                    {{ t('tickets.import.truncated', { n: mapped.rows.length }) }}
                </div>
            </div>

            <div
                class="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 md:static sticky bottom-0 z-10 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            >
                <BBtn severity="secondary" :label="t('tickets.import.backToMapping')" @click="step = 'mapping'" />
                <BBtn :disabled="!canImport" @click="onConfirmImport">
                    {{ t('tickets.import.confirmImport', { n: mapped.rows.length }) }}
                </BBtn>
            </div>
        </div>

        <!-- 4. Průběh -->
        <div v-if="step === 'progress'" class="bg-white rounded-12 border border-gray-200 p-6 md:p-12 text-center">
            <div
                class="inline-block w-12 h-12 border-4 border-teal border-t-transparent rounded-full animate-spin mb-4"
            />
            <p class="text-sm text-gray-600">{{ t('tickets.import.importing', { n: mapped.rows.length }) }}</p>
        </div>

        <!-- 5. Výsledek -->
        <div v-if="step === 'result' && result" class="bg-white rounded-12 border border-gray-200 p-4 md:p-8">
            <div class="text-center">
                <CheckCircle2 v-if="result.ok" class="w-16 h-16 mx-auto text-green mb-4" />
                <AlertCircle v-else class="w-16 h-16 mx-auto text-red mb-4" />
                <h2 class="text-lg font-semibold text-ink mb-2">
                    {{ result.ok ? t('tickets.import.success') : t('tickets.import.failedNothingSaved') }}
                </h2>
            </div>

            <!-- Úspěch: co přesně vzniklo -->
            <dl v-if="result.ok" class="max-w-sm mx-auto mt-6 text-sm space-y-1.5">
                <div class="flex justify-between">
                    <dt class="text-gray-600">{{ t('tickets.import.result.created') }}</dt>
                    <dd class="font-semibold tabular-nums">{{ result.created }}</dd>
                </div>
                <div v-if="result.skipped" class="flex justify-between">
                    <dt class="text-gray-600">{{ t('tickets.import.result.skipped') }}</dt>
                    <dd class="font-semibold tabular-nums">{{ result.skipped }}</dd>
                </div>
                <div class="flex justify-between">
                    <dt class="text-gray-600">{{ t('tickets.import.result.ticketsetsCreated') }}</dt>
                    <dd class="font-semibold tabular-nums">{{ result.ticketsetsCreated }}</dd>
                </div>
                <div class="flex justify-between">
                    <dt class="text-gray-600">{{ t('tickets.import.result.pricesCreated') }}</dt>
                    <dd class="font-semibold tabular-nums">{{ result.pricesCreated }}</dd>
                </div>
            </dl>

            <!-- Neúspěch: musí být jasné, že se neuložilo NIC -->
            <template v-else>
                <p class="text-sm text-gray-600 text-center max-w-lg mx-auto">
                    {{ t('tickets.import.result.atomicHint', { n: result.errorCount }) }}
                </p>

                <div v-if="result.errors.length" class="mt-4 border border-gray-200 rounded-8 overflow-hidden">
                    <div class="max-h-64 overflow-auto">
                        <table class="text-xs w-full">
                            <thead class="bg-gray-50 sticky top-0">
                                <tr>
                                    <th class="px-3 py-2 text-left w-20">{{ t('tickets.import.preview.row') }}</th>
                                    <th class="px-3 py-2 text-left">{{ t('tickets.import.preview.errors') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(e, i) in result.errors" :key="i" class="border-b border-gray-200">
                                    <td class="px-3 py-1.5 tabular-nums">{{ e.row }}</td>
                                    <td class="px-3 py-1.5 text-red">{{ e.message }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="result.errorCount > result.errors.length"
                        class="px-3 py-2 text-xs text-gray-500 bg-gray-50 text-center"
                    >
                        {{
                            t('tickets.import.result.errorsTruncated', {
                                shown: result.errors.length,
                                total: result.errorCount
                            })
                        }}
                    </div>
                </div>

                <div v-if="result.errors.length" class="flex justify-center mt-3">
                    <BBtn severity="secondary" @click="downloadErrors">
                        <Download class="w-4 h-4 mr-1" />
                        {{ t('tickets.import.result.downloadErrors') }}
                    </BBtn>
                </div>
            </template>

            <div class="flex items-center justify-center gap-2 mt-6 flex-wrap">
                <BBtn v-if="!result.ok" severity="secondary" @click="backToMapping">
                    {{ t('tickets.import.backToMapping') }}
                </BBtn>
                <BBtn severity="secondary" :label="t('tickets.import.anotherFile')" @click="reset" />
                <BBtn @click="router.push('/tickets')">{{ t('tickets.import.backToList') }}</BBtn>
            </div>
        </div>
    </DefaultLayout>
</template>
